/**
 * Result detail page - displays detailed fact verification results with human validation
 */

import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { resultsApi, favouritesApi, tagsApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { FactValidationCard } from '../components/FactValidationCard'
import type { HumanValidationInput } from '../types'

export const ResultDetail = () => {
  const { resultId } = useParams<{ resultId: string }>()
  const { user, isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [shareUrl, setShareUrl] = useState<string | null>(null)

  const {
    data: result,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['result', resultId],
    queryFn: () => resultsApi.getResult(resultId!),
    enabled: !!resultId,
  })

  const validationMutation = useMutation({
    mutationFn: (validation: HumanValidationInput) =>
      resultsApi.addHumanValidation(resultId!, validation),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['result', resultId] })
    },
  })

  const visibilityMutation = useMutation({
    mutationFn: (isPublic: boolean) => resultsApi.setVisibility(resultId!, isPublic),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['result', resultId] })
      if (data.share_url) {
        setShareUrl(data.share_url)
      } else {
        setShareUrl(null)
      }
    },
  })

  // Favourite state
  const [isFavourite, setIsFavourite] = useState(false)
  const [favouriteLoaded, setFavouriteLoaded] = useState(false)

  // Tags state
  const [resultTags, setResultTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')

  // Fetch favourite/tag status via dedicated meta endpoint
  useQuery({
    queryKey: ['resultMeta', resultId],
    queryFn: async () => {
      const meta = await resultsApi.getResultMeta(resultId!)
      setIsFavourite(meta.is_favourite)
      setResultTags(meta.tags)
      setFavouriteLoaded(true)
      return meta
    },
    enabled: !!resultId && isAuthenticated,
  })

  // Fetch user tags for autocomplete
  const { data: userTagsData } = useQuery({
    queryKey: ['userTags'],
    queryFn: () => tagsApi.listUserTags(),
    enabled: isAuthenticated,
  })
  const allUserTags = userTagsData?.tags ?? []

  const favouriteMutation = useMutation({
    mutationFn: () => favouritesApi.toggleFavourite(resultId!),
    onSuccess: (data) => {
      setIsFavourite(data.is_favourite)
      queryClient.invalidateQueries({ queryKey: ['results'] })
      queryClient.invalidateQueries({ queryKey: ['resultMeta', resultId] })
    },
    onError: (error) => console.error('Failed to toggle favourite:', error),
  })

  const addTagMutation = useMutation({
    mutationFn: (tagName: string) => tagsApi.addTag(resultId!, tagName),
    onSuccess: (data) => {
      setResultTags(data.tags)
      setTagInput('')
      queryClient.invalidateQueries({ queryKey: ['results'] })
      queryClient.invalidateQueries({ queryKey: ['userTags'] })
      queryClient.invalidateQueries({ queryKey: ['resultMeta', resultId] })
    },
    onError: (error) => console.error('Failed to add tag:', error),
  })

  const removeTagMutation = useMutation({
    mutationFn: (tagName: string) => tagsApi.removeTag(resultId!, tagName),
    onSuccess: (data) => {
      setResultTags(data.tags)
      queryClient.invalidateQueries({ queryKey: ['results'] })
      queryClient.invalidateQueries({ queryKey: ['userTags'] })
      queryClient.invalidateQueries({ queryKey: ['resultMeta', resultId] })
    },
    onError: (error) => console.error('Failed to remove tag:', error),
  })

  if (isLoading) {
    return <div>Loading result details...</div>
  }

  if (error) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ color: '#e74c3c', marginBottom: '1rem' }}>
          Error loading result: {error.message}
        </div>
        <Link
          to="/results"
          style={{
            color: '#3498db',
            textDecoration: 'none',
          }}
        >
          ← Back to Results
        </Link>
      </div>
    )
  }

  if (!result) {
    return <div>Result not found</div>
  }

  const validationResults = result.validation_results || []

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <Link
        to="/results"
        style={{
          color: '#3498db',
          textDecoration: 'none',
          marginBottom: '1rem',
          display: 'inline-block',
        }}
      >
        ← Back to Results
      </Link>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{ flex: 1, margin: '1rem 0' }}>
          {result.document_citation_metadata?.title ? (
            <div>
              <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.6rem', color: '#2c3e50' }}>
                {result.document_citation_metadata.title}
              </h1>
              <div style={{ fontSize: '0.95rem', color: '#555', lineHeight: 1.6 }}>
                {result.document_citation_metadata.authors && (
                  <span>{result.document_citation_metadata.authors}</span>
                )}
                {result.document_citation_metadata.journal && (
                  <span>
                    {result.document_citation_metadata.authors ? ' — ' : ''}
                    <em>{result.document_citation_metadata.journal}</em>
                  </span>
                )}
                {result.document_citation_metadata.year && (
                  <span> ({result.document_citation_metadata.year})</span>
                )}
                {result.document_citation_metadata.volume && (
                  <span>
                    , {result.document_citation_metadata.volume}
                    {result.document_citation_metadata.issue &&
                      `(${result.document_citation_metadata.issue})`}
                  </span>
                )}
                {result.document_citation_metadata.pages && (
                  <span>: {result.document_citation_metadata.pages}</span>
                )}
              </div>
              {result.document_citation_metadata.doi && (
                <a
                  href={`https://doi.org/${result.document_citation_metadata.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#3498db',
                    fontSize: '0.85rem',
                    marginTop: '0.25rem',
                    display: 'inline-block',
                  }}
                >
                  DOI: {result.document_citation_metadata.doi}
                </a>
              )}
            </div>
          ) : (
            <h1 style={{ margin: 0 }}>{result.document_title || 'Fact Verification Result'}</h1>
          )}
        </div>
        {isAuthenticated && favouriteLoaded && (
          <button
            onClick={() => favouriteMutation.mutate()}
            disabled={favouriteMutation.isPending}
            style={{
              background: 'none',
              border: 'none',
              cursor: favouriteMutation.isPending ? 'wait' : 'pointer',
              fontSize: '1.5rem',
              lineHeight: 1,
              color: isFavourite ? '#f39c12' : '#bdc3c7',
              padding: 0,
              marginTop: '1rem',
              transition: 'color 0.15s',
            }}
            title={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
          >
            {isFavourite ? '\u2605' : '\u2606'}
          </button>
        )}
      </div>

      {/* Metadata */}
      <div
        style={{
          background: '#ecf0f1',
          padding: '1.5rem',
          borderRadius: '8px',
          marginBottom: '2rem',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
          }}
        >
          <div>
            <strong>ID:</strong> {result.display_id}
          </div>
          <div>
            <strong>Created:</strong> {new Date(result.created_at).toLocaleString()}
          </div>
          <div>
            <strong>Processing Time:</strong> {result.processing_time_seconds.toFixed(2)}s
          </div>
          <div>
            <strong>Status:</strong>{' '}
            <span
              style={{
                color: result.success ? '#27ae60' : '#e74c3c',
                fontWeight: 'bold',
              }}
            >
              {result.success ? 'Success' : 'Failed'}
            </span>
          </div>
        </div>

        {/* Visibility Toggle - Show controls for authenticated owners */}
        {isAuthenticated && (
          <div
            style={{
              marginTop: '1rem',
              padding: '1rem',
              background: result.is_public ? '#e8f5e9' : '#fff3e0',
              borderRadius: '6px',
              border: `1px solid ${result.is_public ? '#81c784' : '#ffb74d'}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <strong>Visibility:</strong>
                <span style={{ color: result.is_public ? '#2e7d32' : '#e65100' }}>
                  {result.is_public ? '🌐 Public' : '🔒 Private'}
                </span>
              </div>
              <button
                onClick={() => visibilityMutation.mutate(!result.is_public)}
                disabled={visibilityMutation.isPending}
                style={{
                  padding: '0.4rem 0.8rem',
                  background: result.is_public ? '#ff9800' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: visibilityMutation.isPending ? 'wait' : 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                {visibilityMutation.isPending
                  ? 'Updating...'
                  : result.is_public
                    ? 'Make Private'
                    : 'Make Public'}
              </button>
              {result.is_public && (
                <button
                  onClick={() => {
                    const url = shareUrl || `${window.location.origin}/results/${resultId}`
                    navigator.clipboard.writeText(url)
                    alert('Share link copied to clipboard!')
                  }}
                  style={{
                    padding: '0.4rem 0.8rem',
                    background: '#2196f3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  Copy Share Link
                </button>
              )}
            </div>
            {result.is_public && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#555' }}>
                Anyone with the link can view this result (but cannot modify it).
              </div>
            )}
          </div>
        )}

        {/* Public indicator for unauthenticated viewers */}
        {!isAuthenticated && result.is_public && (
          <div
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              background: '#e3f2fd',
              borderRadius: '6px',
              border: '1px solid #90caf9',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>🌐</span>
            <span style={{ color: '#1565c0', fontWeight: 500 }}>
              This is a publicly shared fact verification result
            </span>
          </div>
        )}

        {/* Tags */}
        {isAuthenticated && (
          <div style={{ marginTop: '1rem' }}>
            <strong>Tags:</strong>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                marginTop: '0.5rem',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {resultTags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.3rem',
                    background: '#eef2f7',
                    color: '#4a6785',
                    padding: '0.25rem 0.6rem',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                  }}
                >
                  {tag}
                  <button
                    onClick={() => removeTagMutation.mutate(tag)}
                    disabled={removeTagMutation.isPending}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#95a5a6',
                      fontSize: '0.9rem',
                      lineHeight: 1,
                      padding: '0 0.1rem',
                    }}
                    title={`Remove tag "${tag}"`}
                  >
                    &times;
                  </button>
                </span>
              ))}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <input
                  type="text"
                  list="tag-suggestions"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && tagInput.trim()) {
                      e.preventDefault()
                      addTagMutation.mutate(tagInput.trim())
                    }
                  }}
                  placeholder="Add tag..."
                  style={{
                    padding: '0.25rem 0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    outline: 'none',
                    width: '120px',
                  }}
                />
                <datalist id="tag-suggestions">
                  {allUserTags
                    .filter((t) => !resultTags.includes(t))
                    .map((tag) => (
                      <option key={tag} value={tag} />
                    ))}
                </datalist>
              </div>
            </div>
          </div>
        )}

        {result.error_message && (
          <div style={{ marginTop: '1rem', color: '#e74c3c' }}>
            <strong>Error:</strong> {result.error_message}
          </div>
        )}

        {/* Validation Metadata - Only show HTTP errors or source URL */}
        {result.metadata && result.metadata.source_url && (
          <>
            {/* Show error box if validation failed (includes DNS failures, 404s, etc.) */}
            {!result.success && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: result.metadata.http_status_code === 404 ? '#fff3cd' : '#fadbd8',
                  borderRadius: '4px',
                  border: `1px solid ${result.metadata.http_status_code === 404 ? '#f39c12' : '#e74c3c'}`,
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem', color: '#2c3e50' }}>
                  {result.metadata.http_status_code === 404 ? '⚠️ ' : '❌ '}
                  {result.metadata.http_status_interpretation || 'URL validation failed'}
                </div>
                <div style={{ fontSize: '0.9rem' }}>
                  <strong>Source URL:</strong>{' '}
                  <a
                    href={result.metadata.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#3498db', wordBreak: 'break-all' }}
                  >
                    {result.metadata.source_url}
                  </a>
                </div>
                {result.metadata.http_status_code && (
                  <div style={{ fontSize: '0.85rem', color: '#555', marginTop: '0.5rem' }}>
                    HTTP Status: {result.metadata.http_status_code}
                  </div>
                )}
                {result.metadata.can_retry && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#555' }}>
                    💡 This validation can be retried manually if the source becomes available
                  </div>
                )}
              </div>
            )}

            {/* Show source URL in success case */}
            {result.success && (
              <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                <strong>✅ Source URL:</strong>{' '}
                <a
                  href={result.metadata.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#3498db', wordBreak: 'break-all' }}
                >
                  {result.metadata.source_url}
                </a>
              </div>
            )}
          </>
        )}

        {result.source_urls && result.source_urls.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <strong>Source URLs:</strong>
            <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
              {result.source_urls.map((url, idx) => (
                <li key={idx}>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#3498db' }}
                  >
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Facts */}
      <h2>Facts ({validationResults.length})</h2>

      {validationResults.length === 0 ? (
        <div
          style={{
            background: '#ecf0f1',
            padding: '2rem',
            borderRadius: '8px',
            textAlign: 'center',
          }}
        >
          <p>No facts to display</p>
        </div>
      ) : (
        <div>
          {validationResults.map((fact, idx) => (
            <FactValidationCard
              key={fact.fact_key || idx}
              fact={fact}
              resultId={resultId!}
              userEmail={user?.email}
              fallbackSourceUrl={result.metadata?.source_url ?? result.source_urls?.[0]}
              onValidate={async (validation) => {
                await validationMutation.mutateAsync(validation)
              }}
              onSourceProvided={() => {
                queryClient.invalidateQueries({ queryKey: ['result', resultId] })
              }}
            />
          ))}
        </div>
      )}

      {/* View Original HTML */}
      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <a
          href={`/display/${resultId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#3498db',
            textDecoration: 'none',
          }}
        >
          View Original HTML Report →
        </a>
      </div>
    </div>
  )
}
